import { fmtDate } from '../../utils/datetime';
import React, { useState, useEffect } from "react";
import { toast } from '../../utils/toast';
import * as Yup from "yup";
import { useFormik } from "formik";

import { Users, Plus, Edit3, Trash2, X, User } from "lucide-react";
import { createNewDesignation, deleteDesignation, editDesignation, getAllDesignations } from "../../api/master";
import { useAuth } from "../../context/AuthContext";

interface Designation {
  id: number;
  name: string;
  rank: number;
  updated_at?: string
}
// Move Modal component OUTSIDE of Master component
const Modal = ({ isOpen, onClose, title, children }: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};
const Designation: React.FC = () => {
  const [designation, setDesignation] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentDesignationId, setCurrentDesignationId] = useState(0);
  const [formData, setFormData] = useState({
    name: "",
  });
  const { user } = useAuth();

  useEffect(() => {
    if (user && user.tenant_id) {
      getDesignations();
    }
  }, [user]);


  /**Add New Designation */
  const initialValues = {
    name: "",
    rank: 0
  };

  const designationSchema = Yup.object({
    name: Yup.string().required("Please enter Designation name"),
  });
  const { setValues, values, handleBlur, handleChange, handleSubmit, errors, touched } =
    useFormik({
      initialValues,
      validationSchema: designationSchema,
      onSubmit: async (values) => {
        if (!isEdit) {
          try {
            console.log(values.rank)
            const response = await createNewDesignation(values.name, user.tenant_id, values.rank)

            if (response.status === 200) {
              setIsAddModalOpen(false);
              getDesignations();
              setValues({ name: '', rank: 0 });
              toast.success("Designation Created Successfully!");
            }
          } catch (error: any) {
            toast.error(error.message || "Something went wrong.");
          }
        }

        if (isEdit) {
          try {
            const response = await editDesignation(values.name, user.tenant_id, currentDesignationId);

            if (response.status === 200) {
              getDesignations();
              setIsEditModalOpen(false);
              setValues({ name: '', rank: 0 });
              toast.success("Designation Updated Successfully!");
            }
          } catch (error: any) {
            toast.error(error.message || "Something went wrong.");
          }
        }

      },
    });

  // console.log("designations", designation);
  const getDesignations = async () => {
    try {
      const response = await getAllDesignations(user.tenant_id);
      setDesignation(response);
    } catch (error) {
      console.error("Get designation error:", error);
    }
  };



  const handleEdit = (item: Designation) => {

    setValues({ name: item.name, rank: item.rank })
    setCurrentDesignationId(item.id)
    setIsEditModalOpen(true);
    setIsEdit(true);
  };



  const handleDelete = async (id: number) => {
    if (!await toast.confirm("This action cannot be undone.", "Delete designation?", "Delete")) return;
    try {
      await deleteDesignation(id);
      setDesignation(designation.filter((designation: Designation) => designation.id !== id));
      toast.success("Designation deleted successfully.");
    } catch (error: any) {
      toast.error(error.message || "Failed to delete designation.");
    }
  };



  return (
    <div className="p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Designation Master
            </h1>
            <p className="text-blue-600 mt-1">Manage Designation</p>
          </div>
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Designation
          </button>
        </div>

        {/* Master Data List */}
        <div className="bg-white rounded-xl shadow-md">
          <div className="p-6">
            {/* Header */}
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />Designation ({designation.length})
            </h3>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-700">
                      SN
                    </th>


                    <th className="text-left py-3 px-4 font-medium text-gray-700">
                      Name
                    </th>

                    <th className="text-left py-3 px-4 font-medium text-gray-700">
                      Rank
                    </th>

                    <th className="text-left py-3 px-4 font-medium text-gray-700">
                      Updated At
                    </th>

                    <th className="text-left py-3 px-4 font-medium text-gray-700">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {designation.map((item: Designation, index) => (
                    <tr
                      key={item.id}
                      className="border-b border-gray-100 hover:bg-blue-50 transition-colors"
                    >
                      <td className="py-4 px-4">
                        <div className="flex items-center">
                          <div className="font-medium text-gray-900">
                            {index + 1}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center">
                          <div className="font-medium text-gray-900">
                            {item.name}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center">
                          <div className="font-medium text-gray-900">
                            {item.rank}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center">
                          <div className="font-medium text-gray-900">
                            {fmtDate(item.updated_at)}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(item)}
                            className="p-2 hover:bg-gray-100 rounded border border-gray-300 transition-colors"
                            title="Edit"
                            type="submit"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="p-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Add Modal */}
        <Modal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          title={`Add New Designation`}
        >
          <div className="space-y-4">
            <form onSubmit={handleSubmit}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  name="name"
                  value={values.name}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {errors.name && touched.name && (
                  <p className="text-red-500">{errors.name}</p>
                )}
                <label className="block text-sm font-medium text-gray-700 mb-1 mt-4">
                  Rank *
                </label>
                <input
                  name="rank"
                  value={values.rank}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {errors.rank && touched.rank && (
                  <p className="text-red-500">{errors.rank}</p>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add Designation
                </button>
              </div>
            </form>
          </div>
        </Modal>

        {/* Edit Modal */}
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          title="Edit Designation"
        >
          <div className="space-y-4">
            <form onSubmit={handleSubmit}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={values.name}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <label className="block text-sm font-medium text-gray-700 mb-1 mt-2">
                  Rank *
                </label>
                <input
                  type="text"
                  name="rank"
                  value={values.rank}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button


                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default Designation;
